import { Link, useParams } from 'react-router-dom'

import { fetchPrinciples, fetchResource } from '@/api'
import {
    BackLink,
    Chip,
    DefinitionItem,
    ErrorState,
    Loading,
    PageShell,
    PrincipleChip,
    ScopeChip,
    Section,
    StatusChip,
    VerificationList,
} from '@/components/common'
import { useAsync } from '@/hooks'
import { useI18n } from '@/i18n'

export function ResourceDetail() {
    const { resourceId = '' } = useParams()
    const { t, localized } = useI18n()
    const resource = useAsync(() => fetchResource(resourceId), [resourceId])
    const principles = useAsync(fetchPrinciples, [])

    if (resource.error) {
        return (
            <PageShell>
                <BackLink to="/resources" label={t('nav.resources')} />
                <ErrorState error={resource.error} onRetry={resource.reload} />
            </PageShell>
        )
    }
    if (!resource.data) {
        return (
            <PageShell>
                <Loading />
            </PageShell>
        )
    }

    const data = resource.data
    const principleTitles = new Map((principles.data ?? []).map((item) => [item.id, item]))

    return (
        <PageShell>
            <BackLink to="/resources" label={t('nav.resources')} />

            <div className="detail__head">
                <h1>
                    <span className="item__icon">{data.type_detail?.icon}</span> {data.name}
                    {data.acronym && data.acronym !== data.name ? (
                        <span style={{ color: 'var(--text-muted)' }}> · {data.acronym}</span>
                    ) : null}
                </h1>
                <p className="lead">{localized(data.description)}</p>
            </div>

            <div className="card">
                <dl className="definition">
                    <DefinitionItem term={t('resource.category')}>
                        {data.type_detail ? localized(data.type_detail.label) : data.type}
                    </DefinitionItem>
                    <DefinitionItem term={t('resource.scope')}>
                        <ScopeChip scope={data.scope} />
                    </DefinitionItem>
                    {data.provider_detail ? (
                        <DefinitionItem term={t('resource.provider')}>
                            {data.provider_detail.url ? (
                                <a
                                    href={data.provider_detail.url}
                                    target="_blank"
                                    rel="noreferrer noopener"
                                >
                                    {localized(data.provider_detail.name)}
                                </a>
                            ) : (
                                localized(data.provider_detail.name)
                            )}
                        </DefinitionItem>
                    ) : null}
                    {data.url ? (
                        <DefinitionItem term={t('common.website')}>
                            <a href={data.url} target="_blank" rel="noreferrer noopener">
                                {new URL(data.url).hostname}
                            </a>
                        </DefinitionItem>
                    ) : null}
                    {data.identifier ? (
                        <DefinitionItem term={t('resource.identifier')}>
                            <a href={data.identifier} target="_blank" rel="noreferrer noopener">
                                {data.identifier}
                            </a>
                        </DefinitionItem>
                    ) : null}
                </dl>

                {data.principles.length ? (
                    <div style={{ marginTop: '1rem' }}>
                        <p className="label">{t('resource.principles')}</p>
                        <div className="chips" style={{ marginTop: '0.3rem' }}>
                            {data.principles.map((principle) => (
                                <Link key={principle} to={`/resources?principles=${principle}`}>
                                    <PrincipleChip
                                        id={principle}
                                        group={principleTitles.get(principle)?.group}
                                    />
                                </Link>
                            ))}
                        </div>
                    </div>
                ) : null}

                {data.tags.length ? (
                    <div className="chips" style={{ marginTop: '0.8rem' }}>
                        {data.tags.map((tag) => (
                            <Chip key={tag} variant="outline">
                                #{tag}
                            </Chip>
                        ))}
                    </div>
                ) : null}
            </div>

            <Section title={t('verification.title')} hint={t('verification.hint')}>
                <div className="card">
                    <VerificationList items={data.verification_detail} />
                </div>
            </Section>

            <Section title={`${t('resource.declaredBy')} (${data.usage_count})`}>
                <div className="card">
                    {data.usage.length ? (
                        data.usage.map((item, index) => (
                            <div className="usage" key={`${item.fip}-${item.principle}-${index}`}>
                                <div className="usage__head">
                                    <Link to={`/profiles/${item.fip}`}>
                                        {localized(item.fip_title)}
                                    </Link>
                                    <PrincipleChip
                                        id={item.principle}
                                        group={principleTitles.get(item.principle)?.group}
                                    />
                                    <StatusChip status={item.status} />
                                </div>
                                {item.note ? (
                                    <p className="declaration__note">{localized(item.note)}</p>
                                ) : null}
                            </div>
                        ))
                    ) : (
                        <p className="empty">{t('resource.noUsage')}</p>
                    )}
                </div>
            </Section>
        </PageShell>
    )
}
